import { useId, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface FieldProps {
  label: string;
  /** Подсказка под полем; скрывается, когда показана ошибка. */
  hint?: string;
  error?: string;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/**
 * Обёртка поля формы: подпись, подсказка и ошибка.
 *
 * Связывает подпись с полем и сообщает об ошибке через `aria-describedby`
 * и `aria-live` — иначе пользователь программы чтения с экрана не узнает,
 * что именно он заполнил неверно.
 */
export function Field({ label, hint, error, required, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-[var(--text-secondary)]">
        {label}
        {required && (
          <span className="ml-1 text-danger-500" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger-500">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Общие классы поля ввода — чтобы все поля выглядели одинаково. */
export function inputClasses(invalid?: boolean, className?: string): string {
  return cn(
    'h-12 w-full rounded-[var(--radius-control)] border bg-[var(--surface-card)] px-4',
    'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
    'transition-colors outline-none',
    invalid ? 'border-danger-500' : 'border-[var(--border-strong)] focus:border-[var(--focus-ring)]',
    'disabled:cursor-not-allowed disabled:opacity-60',
    className,
  );
}
